import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import AuthPage from "@/pages/AuthPage";
import SignupPage from "@/pages/SignupPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import TrackOrderPage from "@/pages/TrackOrderPage";
import { Toaster } from "@/components/ui/sonner";

// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SellersManagement from "@/pages/admin/SellersManagement";
import UsersManagement from "@/pages/admin/UsersManagement";
import AdminPickupLocations from "@/pages/admin/PickupLocations";
import AdminDropLocations from "@/pages/admin/DropLocations";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/OrderDetail";
import AdminCreateOrder from "@/pages/admin/CreateOrder";
import AdminAppointments from "@/pages/admin/AdminAppointments";
import SellerWallet from "@/pages/admin/SellerWallet";
import B2BMarketplace from "@/pages/admin/B2BMarketplace";
import B2BShippingPartner from "@/pages/admin/B2BShippingPartner";
import APILogs from "@/pages/admin/APILogs";
import CommercialStructure from "@/pages/admin/CommercialStructure";
import WalletTransactions from "@/pages/admin/WalletTransactions";
import PnLReport from "@/pages/admin/PnLReport";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminSupport from "@/pages/admin/Support";
import AdminSupportDetail from "@/pages/admin/SupportDetail";
import ShopifyDevDocs from "@/pages/admin/ShopifyDevDocs";
import WooCommerceDevDocs from "@/pages/admin/WooCommerceDevDocs";
import MagentoDevDocs from "@/pages/admin/MagentoDevDocs";
import CustomApiDevDocs from "@/pages/admin/CustomApiDevDocs";
import AdminBulkOrders from "@/pages/admin/BulkOrders";
import AdminBulkOperations from "@/pages/admin/BulkOperations";
import AdminPickupRequests from "@/pages/admin/AdminPickupRequests";
import AdminNotificationSetup from "@/pages/admin/NotificationSetup";
import AdminWhatsAppNotificationSetup from "@/pages/admin/WhatsAppNotificationSetup";
import AdminRedFlags from "@/pages/admin/RedFlags";
import AdminCommercialRedFlags from "@/pages/admin/CommercialRedFlags";
import AdminRateErrors from "@/pages/admin/RateErrors";
import AdminReports from "@/pages/admin/AdminReports";
import RolesPermissions from "@/pages/admin/RolesPermissions";
import B2CPartners from "@/pages/admin/b2c/B2CPartners";
import B2CPlans from "@/pages/admin/b2c/B2CPlans";
import B2CZones from "@/pages/admin/b2c/B2CZones";
import B2CRecommendationEngine from "@/pages/admin/b2c/B2CRecommendationEngine";
import AdminB2COrders from "@/pages/admin/b2c/B2COrders";
import B2CAdminCreateOrder from "@/pages/admin/b2c/B2CAdminCreateOrder";
import B2CAdminOrderDetail from "@/pages/admin/b2c/B2CAdminOrderDetail";
import AdminB2CReturns from "@/pages/admin/b2c/B2CReturns";
import B2CAdminReturnDetail from "@/pages/admin/b2c/B2CAdminReturnDetail";
import AdminB2CCod from "@/pages/admin/b2c/B2CCodReconciliation";
import AdminB2CEarlyCod from "@/pages/admin/b2c/B2CEarlyCod";
import AdminB2CNdr from "@/pages/admin/b2c/B2CNdr";
import AdminB2CWeightDiscrepancy from "@/pages/admin/b2c/B2CWeightDiscrepancy";
import B2CKYCQueue from "@/pages/admin/b2c/B2CKYCQueue";
import B2CKyc from "@/pages/seller/b2c/B2CKyc";
import B2CAdminDashboard from "@/pages/admin/b2c/B2CAdminDashboard";
import AdminB2CWebhooks from "@/pages/admin/b2c/B2CWebhooks";

// Shared Pages
import ProfilePage from "@/pages/ProfilePage";

// Seller Pages
import SellerDashboard from "@/pages/seller/SellerDashboard";
import SellerPickupLocations from "@/pages/seller/PickupLocations";
import SellerDropLocations from "@/pages/seller/DropLocations";
import SellerOrders from "@/pages/seller/SellerOrders";
import SellerUsersManagement from "@/pages/seller/UsersManagement";
import SellerOrderDetail from "@/pages/seller/OrderDetail";
import CreateOrder from "@/pages/seller/CreateOrder";
import SellerAppointments from "@/pages/seller/SellerAppointments";
import SellerWalletPage from "@/pages/seller/SellerWalletPage";
import SellerBulkOrders from "@/pages/seller/BulkOrders";
import SellerPickupRequests from "@/pages/seller/SellerPickupRequests";
import SellerNotificationSetup from "@/pages/seller/SellerNotificationSetup";
import SellerReports from "@/pages/seller/SellerReports";
import SellerInvoicesPage from "@/pages/seller/SellerInvoicesPage";
import SellerSupport from "@/pages/seller/Support";
import SellerSupportCreate from "@/pages/seller/SupportCreate";
import SellerSupportDetail from "@/pages/seller/SupportDetail";
import SellerTeamRoles from "@/pages/seller/SellerTeamRoles";
import B2CDashboard from "@/pages/seller/b2c/B2CDashboard";
import B2COrders from "@/pages/seller/b2c/B2COrders";
import B2CCreateOrder from "@/pages/seller/b2c/B2CCreateOrder";
import B2CBulkOrders from "@/pages/seller/b2c/B2CBulkOrders";
import B2COrderDetail from "@/pages/seller/b2c/B2COrderDetail";
import B2CReturns from "@/pages/seller/b2c/B2CReturns";
import B2CCreateReturn from "@/pages/seller/b2c/B2CCreateReturn";
import B2CReturnDetail from "@/pages/seller/b2c/B2CReturnDetail";
import B2CCod from "@/pages/seller/b2c/B2CCodReconciliation";
import B2CEarlyCod from "@/pages/seller/b2c/B2CEarlyCod";
import B2CNdr from "@/pages/seller/b2c/B2CNdr";
import B2CWeightDiscrepancy from "@/pages/seller/b2c/B2CWeightDiscrepancy";
import B2CChannels from "@/pages/seller/b2c/B2CChannels";
import B2CRateCalculator from "@/pages/seller/b2c/B2CRateCalculator";
import B2CPlan from "@/pages/seller/b2c/B2CPlan";
import B2CWebhook from "@/pages/seller/b2c/B2CWebhook";
import CourierSelectionCriteria from "@/pages/seller/CourierSelectionCriteria";
import SellerZoneDefinition from "@/pages/seller/ZoneDefinition";
import SellerB2BRateCard from "@/pages/seller/B2BRateCard";
import DocumentationPage from "@/pages/DocumentationPage";
import TATCalculator from "@/pages/TATCalculator";
import FreightEstimator from "@/pages/FreightEstimator";
import RoutePlanning from "@/pages/RoutePlanning";
import AdminLayout from "@/components/AdminLayout";
import SellerLayout from "@/components/SellerLayout";
import { can } from "@/lib/permissions";
import { useDisplayScale } from "@/hooks/useDisplayScale";

// Pharmeasy Pages
import PharmeasyDashboard from "@/pages/pharmeasy/PharmeasyDashboard";
import PharmeasyOrders from "@/pages/pharmeasy/PharmeasyOrders";
import PharmeasyShipmentDetail from "@/pages/pharmeasy/PharmeasyShipmentDetail";
import PharmeasyHubOps from "@/pages/pharmeasy/PharmeasyHubOps";
import HubLayoutPage from "@/pages/pharmeasy/HubLayoutPage";
import InwardPage from "@/pages/pharmeasy/InwardPage";
import InwardSessionDetail from "@/pages/pharmeasy/InwardSessionDetail";
import InwardPreview from "@/pages/pharmeasy/InwardPreview";
import PharmeasyTrips from "@/pages/pharmeasy/PharmeasyTrips";
import PharmeasyTripDetail from "@/pages/pharmeasy/PharmeasyTripDetail";
import PharmeasyRiders from "@/pages/pharmeasy/PharmeasyRiders";
import PharmeasyRiderLocation from "@/pages/pharmeasy/RiderLocation";
import PharmeasyRiderAttendance from "@/pages/pharmeasy/RiderAttendance";
import PharmeasyApiLogs from "@/pages/pharmeasy/PharmeasyApiLogs";
import PharmeasyCronJobs from "@/pages/pharmeasy/PharmeasyCronJobs";
import PharmeasyBilling from "@/pages/pharmeasy/PharmeasyBilling";
import PharmeasyReport from "@/pages/pharmeasy/PharmeasyReport";
import PharmeasyCODReconciliation from "@/pages/pharmeasy/PharmeasyCODReconciliation";
import PharmeasyNotifications from "@/pages/pharmeasy/PharmeasyNotifications";
import PharmeasyRevenueProjection from "@/pages/pharmeasy/PharmeasyRevenueProjection";
import EscalationManagement from "@/pages/pharmeasy/EscalationManagement";
import EscalationDetail from "@/pages/pharmeasy/EscalationDetail";

// Rider Pages
import RiderDashboard from "@/pages/rider/RiderDashboard";
import RiderAttendance from "@/pages/rider/RiderAttendance";
import RiderTrips from "@/pages/rider/RiderTrips";
import RiderHistory from "@/pages/rider/RiderHistory";
import RiderCOD from "@/pages/rider/RiderCOD";

// Hub Operator Pages
import HubOperatorPending from "@/pages/hub-operator/HubOperatorPending";
import HubOperatorInProgress from "@/pages/hub-operator/HubOperatorInProgress";
import HubOperatorCompleted from "@/pages/hub-operator/HubOperatorCompleted";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Ensure HTTPS is used in production
const ensureHttps = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') && !url.includes('localhost')) {
    return url.replace('http://', 'https://');
  }
  return url;
};
export const API = `${ensureHttps(BACKEND_URL)}/api`;

// Axios interceptor for auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios
        .get(`${API}/auth/me`)
        .then((res) => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem("token");
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  // Neutralize Windows display scaling (125/150%) on desktop portals so pages
  // render like 100% (see lib/displayScale). Mac/Retina + rider/hub are untouched.
  useDisplayScale(user);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="text-lg font-medium text-slate-600">Loading...</div>
      </div>
    );
  }

  const isAdmin = user?.role === "admin" || user?.role === "admin_user" || user?.role === "account_manager";
  const isSeller = user?.role === "seller_admin" || user?.role === "seller_user";
  // RBAC route guards (cosmetic — backend enforces). `user.permissions` rides on
  // both the login response user and /auth/me, so it always reaches this state.
  const userCan = (perm) => can(user, perm);
  // Safe landing for principals whose role lacks dashboard:view (profile is identity, ungated).
  const adminHome = userCan("dashboard:view") ? "/admin" : "/admin/profile";
  const sellerHome = userCan("dashboard:view") ? "/seller" : "/seller/profile";
  const isRider = user?.role === "pharmeasy_rider";
  const isHubOperator = user?.role === "pharmeasy_hub_operator";
  const isPharmeasy = user?.role === "pharmeasy_hub_operator" || user?.role === "pharmeasy_rider" || user?.role === "hub_manager";
  const canAccessPharmeasy = isAdmin || isPharmeasy;
  // Riders should not access admin-only pharmeasy pages
  const canAccessPharmeasyAdmin = isAdmin || user?.role === "pharmeasy_hub_operator" || user?.role === "hub_manager";

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={!user ? <AuthPage setUser={setUser} /> : <Navigate to="/" />}
          />
          {/* Public self-serve signup (B2C Stage D) — render when logged out. */}
          <Route
            path="/signup"
            element={!user ? <SignupPage /> : <Navigate to="/" />}
          />
          {/* Email verification is reachable whether or not logged in (link from email). */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* PUBLIC order tracking — buyer-facing, no auth (opens in its own tab from login). */}
          <Route path="/track" element={<TrackOrderPage />} />

          {/* Admin Routes — each guarded by the same permission as its nav item
              (RBAC_MIGRATION_MAP.md frontend contract); unauthorized paths fall
              through to the catch-all redirect below. */}
          {isAdmin && (
            <>
              <Route path="/" element={<Navigate to={adminHome} />} />
              {userCan("dashboard:view") && <Route path="/admin" element={<AdminDashboard user={user} onLogout={handleLogout} />} />}
              {userCan("sellers:view") && <Route path="/admin/sellers" element={<SellersManagement user={user} onLogout={handleLogout} />} />}
              {userCan("users:view") && <Route path="/admin/users" element={<UsersManagement user={user} onLogout={handleLogout} />} />}
              {userCan("locations:view") && <Route path="/admin/pickup-locations" element={<AdminPickupLocations user={user} onLogout={handleLogout} />} />}
              {userCan("locations:view") && <Route path="/admin/drop-locations" element={<AdminDropLocations user={user} onLogout={handleLogout} />} />}
              {userCan("orders:view") && <Route path="/admin/orders" element={<AdminOrders user={user} onLogout={handleLogout} />} />}
              {userCan("orders:view") && <Route path="/admin/orders/:orderId" element={<AdminOrderDetail user={user} onLogout={handleLogout} />} />}
              {userCan("orders:create") && <Route path="/admin/orders/create" element={<AdminCreateOrder user={user} onLogout={handleLogout} />} />}
              {userCan("wallets:view") && <Route path="/admin/sellers/:sellerId/wallet" element={<SellerWallet user={user} onLogout={handleLogout} />} />}
              {userCan("wallets:view") && <Route path="/admin/sellers/:sellerId/wallet/transactions" element={<WalletTransactions user={user} onLogout={handleLogout} />} />}
              {userCan("commercials:view") && <Route path="/admin/sellers/:sellerId/commercial-structure" element={<CommercialStructure user={user} onLogout={handleLogout} />} />}
              {userCan("b2b_marketplace:view") && <Route path="/admin/b2b-marketplace" element={<B2BMarketplace user={user} onLogout={handleLogout} />} />}
              {userCan("b2b_partners:manage") && <Route path="/admin/b2b-shipping-partner" element={<B2BShippingPartner user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:view") && <Route path="/admin/b2c" element={<B2CAdminDashboard user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.partners:manage") && <Route path="/admin/b2c/partners" element={<B2CPartners user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.plans:view") && <Route path="/admin/b2c/plans" element={<B2CPlans user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.zones:manage") && <Route path="/admin/b2c/zones" element={<B2CZones user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.recommendation:manage") && <Route path="/admin/b2c/recommendation" element={<B2CRecommendationEngine user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:view") && <Route path="/admin/b2c/orders" element={<AdminB2COrders user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:create") && <Route path="/admin/b2c/orders/create" element={<B2CAdminCreateOrder user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:view") && <Route path="/admin/b2c/orders/:orderId" element={<B2CAdminOrderDetail user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.returns:view") && <Route path="/admin/b2c/returns" element={<AdminB2CReturns user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.returns:view") && <Route path="/admin/b2c/returns/:returnId" element={<B2CAdminReturnDetail user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.cod:view") && <Route path="/admin/b2c/cod" element={<AdminB2CCod user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.early_cod:manage") && <Route path="/admin/b2c/early-cod" element={<AdminB2CEarlyCod user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.ndr:view") && <Route path="/admin/b2c/ndr" element={<AdminB2CNdr user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.weight_discrepancy:view") && <Route path="/admin/b2c/weight-discrepancies" element={<AdminB2CWeightDiscrepancy user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.kyc:review") && <Route path="/admin/b2c/kyc" element={<B2CKYCQueue user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:view") && <Route path="/admin/b2c/webhooks" element={<AdminB2CWebhooks user={user} onLogout={handleLogout} />} />}
              {userCan("api_logs:view") && <Route path="/admin/api-logs" element={<APILogs user={user} onLogout={handleLogout} />} />}
              {userCan("appointments:view") && <Route path="/admin/appointments" element={<AdminAppointments user={user} onLogout={handleLogout} />} />}
              {userCan("pnl:view") && <Route path="/admin/pnl-report" element={<PnLReport user={user} onLogout={handleLogout} />} />}
              {userCan("billing:view") && <Route path="/admin/billing" element={<AdminBilling user={user} onLogout={handleLogout} />} />}
              {userCan("support:view") && <Route path="/admin/support" element={<AdminSupport user={user} onLogout={handleLogout} />} />}
              {userCan("support:view") && <Route path="/admin/support/:ticketId" element={<AdminSupportDetail user={user} onLogout={handleLogout} />} />}
              {userCan("developer_docs:view") && <Route path="/admin/dev-docs/shopify" element={<ShopifyDevDocs user={user} onLogout={handleLogout} />} />}
              {userCan("developer_docs:view") && <Route path="/admin/dev-docs/woocommerce" element={<WooCommerceDevDocs user={user} onLogout={handleLogout} />} />}
              {userCan("developer_docs:view") && <Route path="/admin/dev-docs/magento" element={<MagentoDevDocs user={user} onLogout={handleLogout} />} />}
              {userCan("developer_docs:view") && <Route path="/admin/dev-docs/custom-api" element={<CustomApiDevDocs user={user} onLogout={handleLogout} />} />}
              {userCan("bulk_orders:view") && <Route path="/admin/bulk-orders" element={<AdminBulkOrders user={user} onLogout={handleLogout} />} />}
              {userCan("bulk_operations:view") && <Route path="/admin/bulk-operations" element={<AdminBulkOperations user={user} onLogout={handleLogout} />} />}
              {userCan("pickup_requests:view") && <Route path="/admin/pickup-requests" element={<AdminPickupRequests user={user} onLogout={handleLogout} />} />}
              {userCan("notifications:view") && <Route path="/admin/notification-setup" element={<AdminNotificationSetup user={user} onLogout={handleLogout} />} />}
              {userCan("notifications:view") && <Route path="/admin/whatsapp-notification-setup" element={<AdminWhatsAppNotificationSetup user={user} onLogout={handleLogout} />} />}
              {userCan("red_flags:view") && <Route path="/admin/red-flags" element={<AdminRedFlags user={user} onLogout={handleLogout} />} />}
              {userCan("red_flags:view") && <Route path="/admin/commercial-red-flags" element={<AdminCommercialRedFlags user={user} onLogout={handleLogout} />} />}
              {userCan("bulk_operations:view") && <Route path="/admin/rate-errors" element={<AdminRateErrors user={user} onLogout={handleLogout} />} />}
              {userCan("reports:view") && <Route path="/admin/reports" element={<AdminReports user={user} onLogout={handleLogout} />} />}
              <Route path="/admin/docs" element={<DocumentationPage user={user} onLogout={handleLogout} Layout={AdminLayout} />} />
              {userCan("tools:use") && <Route path="/admin/tat-calculator" element={<TATCalculator user={user} onLogout={handleLogout} Layout={AdminLayout} isAdmin={true} />} />}
              {userCan("tools:use") && <Route path="/admin/freight-estimator" element={<FreightEstimator user={user} onLogout={handleLogout} Layout={AdminLayout} isAdmin={true} />} />}
              {userCan("tools:use") && <Route path="/admin/route-planning" element={<RoutePlanning user={user} onLogout={handleLogout} Layout={AdminLayout} />} />}
              {/* Roles & Permissions is hard-locked to the `admin` superuser (never grantable) */}
              {user?.role === "admin" && <Route path="/admin/roles" element={<RolesPermissions user={user} onLogout={handleLogout} />} />}
              <Route path="/admin/profile" element={<ProfilePage user={user} onLogout={handleLogout} />} />
              {/* Unauthorized/unknown paths redirect to the portal root (same pattern as rider/hub-operator) */}
              <Route path="*" element={<Navigate to={adminHome} replace />} />
            </>
          )}
          
          {/* Seller Routes — each guarded by the same permission as its nav item
              (RBAC_MIGRATION_MAP.md frontend contract); unauthorized paths fall
              through to the catch-all redirect below. */}
          {isSeller && (
            <>
              <Route path="/" element={<Navigate to={sellerHome} />} />
              {userCan("dashboard:view") && <Route path="/seller" element={<SellerDashboard user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.dashboard:view") && <Route path="/seller/b2c" element={<B2CDashboard user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:view") && <Route path="/seller/b2c/orders" element={<B2COrders user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:create") && <Route path="/seller/b2c/orders/create" element={<B2CCreateOrder user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.bulk_upload:use") && <Route path="/seller/b2c/orders/bulk" element={<B2CBulkOrders user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.rate_calculator:use") && <Route path="/seller/b2c/rate-calculator" element={<B2CRateCalculator user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:view") && <Route path="/seller/b2c/orders/:orderId" element={<B2COrderDetail user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.returns:view") && <Route path="/seller/b2c/returns" element={<B2CReturns user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.returns:create") && <Route path="/seller/b2c/returns/create" element={<B2CCreateReturn user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.returns:view") && <Route path="/seller/b2c/returns/:returnId" element={<B2CReturnDetail user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.cod:view") && <Route path="/seller/b2c/cod" element={<B2CCod user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.early_cod:view") && <Route path="/seller/b2c/early-cod" element={<B2CEarlyCod user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.ndr:view") && <Route path="/seller/b2c/ndr" element={<B2CNdr user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.weight_discrepancy:view") && <Route path="/seller/b2c/weight-discrepancies" element={<B2CWeightDiscrepancy user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.channels:view") && <Route path="/seller/b2c/channels" element={<B2CChannels user={user} onLogout={handleLogout} />} />}
              {userCan("kyc:submit") && <Route path="/seller/b2c/kyc" element={<B2CKyc user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.plan:manage") && <Route path="/seller/b2c/plan" element={<B2CPlan user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.orders:view") && <Route path="/seller/b2c/webhook" element={<B2CWebhook user={user} onLogout={handleLogout} />} />}
              {userCan("b2c.courier_criteria:manage") && <Route path="/seller/courier-selection-criteria" element={<CourierSelectionCriteria user={user} onLogout={handleLogout} />} />}
              {userCan("locations:view") && <Route path="/seller/pickup-locations" element={<SellerPickupLocations user={user} onLogout={handleLogout} />} />}
              {userCan("locations:view") && <Route path="/seller/drop-locations" element={<SellerDropLocations user={user} onLogout={handleLogout} />} />}
              {userCan("orders:view") && <Route path="/seller/orders" element={<SellerOrders user={user} onLogout={handleLogout} />} />}
              {userCan("users:view") && <Route path="/seller/users" element={<SellerUsersManagement user={user} onLogout={handleLogout} />} />}
              {userCan("orders:view") && <Route path="/seller/orders/:orderId" element={<SellerOrderDetail user={user} onLogout={handleLogout} />} />}
              <Route path="/seller/zone-definition" element={<SellerZoneDefinition user={user} onLogout={handleLogout} />} />
              <Route path="/seller/b2b-rate-card" element={<SellerB2BRateCard user={user} onLogout={handleLogout} />} />
              {userCan("orders:create") && <Route path="/seller/orders/create" element={<CreateOrder user={user} onLogout={handleLogout} />} />}
              {userCan("wallet:view") && <Route path="/seller/wallet" element={<SellerWalletPage user={user} onLogout={handleLogout} />} />}
              {userCan("appointments:view") && <Route path="/seller/appointments" element={<SellerAppointments user={user} onLogout={handleLogout} />} />}
              {userCan("bulk_orders:view") && <Route path="/seller/bulk-orders" element={<SellerBulkOrders user={user} onLogout={handleLogout} />} />}
              {userCan("pickup_requests:view") && <Route path="/seller/pickup-requests" element={<SellerPickupRequests user={user} onLogout={handleLogout} />} />}
              {userCan("notifications:manage") && <Route path="/seller/notification-setup" element={<SellerNotificationSetup user={user} onLogout={handleLogout} />} />}
              {userCan("reports:view") && <Route path="/seller/reports" element={<SellerReports user={user} onLogout={handleLogout} />} />}
              {userCan("invoices:view") && <Route path="/seller/invoices" element={<SellerInvoicesPage user={user} onLogout={handleLogout} />} />}
              {userCan("support:view") && <Route path="/seller/support" element={<SellerSupport user={user} onLogout={handleLogout} />} />}
              {userCan("support:create") && <Route path="/seller/support/create" element={<SellerSupportCreate user={user} onLogout={handleLogout} />} />}
              {userCan("support:view") && <Route path="/seller/support/:ticketId" element={<SellerSupportDetail user={user} onLogout={handleLogout} />} />}
              <Route path="/seller/docs" element={<DocumentationPage user={user} onLogout={handleLogout} Layout={SellerLayout} />} />
              {userCan("tools:use") && <Route path="/seller/tat-calculator" element={<TATCalculator user={user} onLogout={handleLogout} Layout={SellerLayout} isAdmin={false} />} />}
              {userCan("tools:use") && <Route path="/seller/freight-estimator" element={<FreightEstimator user={user} onLogout={handleLogout} Layout={SellerLayout} isAdmin={false} />} />}
              {userCan("tools:use") && <Route path="/seller/route-planning" element={<RoutePlanning user={user} onLogout={handleLogout} Layout={SellerLayout} />} />}
              {/* Team Roles is hard-locked to seller_admin (never grantable) */}
              {user?.role === "seller_admin" && <Route path="/seller/roles" element={<SellerTeamRoles user={user} onLogout={handleLogout} />} />}
              <Route path="/seller/profile" element={<ProfilePage user={user} onLogout={handleLogout} />} />
              {/* Unauthorized/unknown paths redirect to the portal root (same pattern as rider/hub-operator) */}
              <Route path="*" element={<Navigate to={sellerHome} replace />} />
            </>
          )}

          {/* Pharmeasy Routes - accessible by admin and pharmeasy roles */}
          {canAccessPharmeasy && (
            <>
              {isPharmeasy && !isRider && !isHubOperator && <Route path="/" element={<Navigate to="/pharmeasy" />} />}
              <Route path="/pharmeasy" element={<PharmeasyDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/dashboard" element={<PharmeasyDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/orders" element={<PharmeasyOrders user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/orders/:shipmentId" element={<PharmeasyShipmentDetail user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/trips" element={<PharmeasyTrips user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/trips/:tripId" element={<PharmeasyTripDetail user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/profile" element={<ProfilePage user={user} onLogout={handleLogout} />} />
            </>
          )}
          
          {/* Admin-only Pharmeasy Routes - NOT accessible by riders */}
          {canAccessPharmeasyAdmin && (
            <>
              <Route path="/pharmeasy/hub-ops" element={<PharmeasyHubOps user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/hub-ops/:hubId/layout" element={<HubLayoutPage user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/hub-ops/:hubId/inward" element={<InwardPage user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/hub-ops/session/:sessionId" element={<InwardSessionDetail user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/hub-ops/inward-preview" element={<InwardPreview user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/riders" element={<PharmeasyRiders user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/riders/:riderId/location" element={<PharmeasyRiderLocation user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/riders/:riderId/attendance" element={<PharmeasyRiderAttendance user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/api-logs" element={<PharmeasyApiLogs user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/cron-jobs" element={<PharmeasyCronJobs user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/billing" element={<PharmeasyBilling user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/billing/settings" element={<Navigate to="/pharmeasy/billing" replace />} />
              <Route path="/pharmeasy/report" element={<PharmeasyReport user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/revenue-projection" element={<PharmeasyRevenueProjection user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/cod-reconciliation" element={<PharmeasyCODReconciliation user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/notifications" element={<PharmeasyNotifications user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/escalations" element={<EscalationManagement user={user} onLogout={handleLogout} />} />
              <Route path="/pharmeasy/escalations/:escalationId" element={<EscalationDetail user={user} onLogout={handleLogout} />} />
            </>
          )}

          {/* Rider Routes - mobile-first experience for pharmeasy_rider role */}
          {isRider && (
            <>
              <Route path="/" element={<Navigate to="/rider" />} />
              <Route path="/rider" element={<RiderDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/rider/attendance" element={<RiderAttendance user={user} onLogout={handleLogout} />} />
              <Route path="/rider/trips" element={<RiderTrips user={user} onLogout={handleLogout} />} />
              <Route path="/rider/history" element={<RiderHistory user={user} onLogout={handleLogout} />} />
              <Route path="/rider/cod" element={<RiderCOD user={user} onLogout={handleLogout} />} />
              {/* Catch-all redirect for riders accessing unauthorized routes */}
              <Route path="*" element={<Navigate to="/rider" />} />
            </>
          )}

          {/* Hub Operator Routes - mobile-first experience for pharmeasy_hub_operator role */}
          {isHubOperator && !isAdmin && (
            <>
              <Route path="/" element={<Navigate to="/hub-operator" />} />
              <Route path="/hub-operator" element={<HubOperatorPending user={user} onLogout={handleLogout} />} />
              <Route path="/hub-operator/in-progress" element={<HubOperatorInProgress user={user} onLogout={handleLogout} />} />
              <Route path="/hub-operator/completed" element={<HubOperatorCompleted user={user} onLogout={handleLogout} />} />
              {/* Catch-all redirect for hub operators accessing unauthorized routes */}
              <Route path="*" element={<Navigate to="/hub-operator" />} />
            </>
          )}
          
          {/* Redirect if not logged in */}
          {!user && <Route path="*" element={<Navigate to="/login" />} />}
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
